import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { debugEnvironmentConfig, validateApiConfig, validateGoogleConfig } from './utils/debugConfig';

import { useAuth } from './contexts/AuthContext';
import { useSubscription } from './hooks/useSubscription';

// Layout components
import PublicLayout from './components/layouts/PublicLayout';
import DashboardLayout from './components/layouts/DashboardLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Loading components
import LoadingSpinner from './components/ui/LoadingSpinner';
import PageLoader from './components/ui/PageLoader';

// Lazy load pages for better performance
const HomePage = React.lazy(() => import('./pages/HomePage'));
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('./pages/auth/VerifyEmailPage'));

const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const CardsPage = React.lazy(() => import('./pages/dashboard/CardsPage'));
const CardEditorPage = React.lazy(() => import('./pages/dashboard/CardEditorPage'));
const QRPage = React.lazy(() => import('./pages/dashboard/QRPage'));
const TemplatesPage = React.lazy(() => import('./pages/dashboard/TemplatesPage'));
const SubdomainsPage = React.lazy(() => import('./pages/dashboard/SubdomainsPage'));
const AnalyticsPage = React.lazy(() => import('./pages/dashboard/AnalyticsPage'));
const SettingsPage = React.lazy(() => import('./pages/dashboard/SettingsPage'));
const PaymentPage = React.lazy(() => import('./pages/dashboard/PaymentPage'));
const NotificationsPage = React.lazy(() => import('./pages/dashboard/NotificationsPage'));
const ProfilesPage = React.lazy(() => import('./pages/dashboard/ProfilesPage'));
const ProfilesEditorPage = React.lazy(() => import('./pages/dashboard/ProfilesEditorPage'));

const PublicCardPage = React.lazy(() => import('./pages/PublicCardPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Legal pages
const TermsPage = React.lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/legal/PrivacyPage'));
const CookiesPage = React.lazy(() => import('./pages/legal/CookiesPage'));

// Protected route component
const ProtectedRoute = ({ children, requireVerification = false }) => {
  const { isAuthenticated, isEmailVerified, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireVerification && !isEmailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  return children;
};

// Public route component (redirect if authenticated)
const PublicRoute = ({ children, redirectTo = '/dashboard' }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

// Protected subscription route component
const ProtectedSubscriptionRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isActive, loading: subscriptionLoading } = useSubscription();

  if (isLoading || subscriptionLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isActive) {
    return <Navigate to="/payments" replace />;
  }

  return children;
};

// App component
function App() {
  const { isLoading } = useAuth();

  // Debug environment configuration
  useEffect(() => {
    // Always run debug in both dev and production
    debugEnvironmentConfig();
    validateApiConfig();
    validateGoogleConfig();
  }, []);

  // Show loading screen while checking authentication
  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <Helmet>
        <html lang="es" />
        <body />
      </Helmet>

      <div className='public-card-social-grid'>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={
              <PublicLayout minimal>
                <HomePage />
              </PublicLayout>
            } />

            {/* Auth routes */}
            <Route path="/login" element={
              <PublicRoute>
                <AuthLayout>
                  <LoginPage />
                </AuthLayout>
              </PublicRoute>
            } />

            <Route path="/register" element={
              <PublicRoute>
                <AuthLayout>
                  <RegisterPage />
                </AuthLayout>
              </PublicRoute>
            } />

            <Route path="/forgot-password" element={
              <PublicRoute>
                <AuthLayout>
                  <ForgotPasswordPage />
                </AuthLayout>
              </PublicRoute>
            } />

            <Route path="/reset-password/:token" element={
              <PublicRoute>
                <AuthLayout>
                  <ResetPasswordPage />
                </AuthLayout>
              </PublicRoute>
            } />

            <Route path="/verify-email/:token?" element={
              <AuthLayout>
                <VerifyEmailPage />
              </AuthLayout>
            } />

            {/* Protected dashboard routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/cards" element={
              <ProtectedRoute requireVerification>
                <DashboardLayout>
                  <CardsPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/profiles" element={
              <ProtectedRoute requireVerification>
                <DashboardLayout>
                  <ProfilesPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/profiles/new" element={
              <ProtectedRoute requireVerification>
                <DashboardLayout>
                  <ProfilesEditorPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/profiles/:id/edit" element={
              <ProtectedRoute requireVerification>
                <DashboardLayout>
                  <ProfilesEditorPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/cards/new" element={
              <ProtectedRoute requireVerification>
                <DashboardLayout>
                  <CardEditorPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/cards/:id/edit" element={
              <ProtectedRoute requireVerification>
                <DashboardLayout>
                  <CardEditorPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/cards/:id/qr" element={
              <ProtectedRoute requireVerification>
                <DashboardLayout>
                  <QRPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/templates" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TemplatesPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />

            {/* <Route path="/subdomains" element={
              <ProtectedSubscriptionRoute>
                <DashboardLayout>
                  <SubdomainsPage />
                </DashboardLayout>
              </ProtectedSubscriptionRoute>
            } /> */}

            {/* <Route path="/analytics" element={
              <ProtectedSubscriptionRoute>
                <DashboardLayout>
                  <AnalyticsPage />
                </DashboardLayout>
              </ProtectedSubscriptionRoute>
            } />

            <Route path="/settings" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SettingsPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/payments" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PaymentPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />

            <Route path="/payment" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PaymentPage />
                </DashboardLayout>
              </ProtectedRoute>
            } /> */}

            <Route path="/notifications" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <NotificationsPage />
                </DashboardLayout>
              </ProtectedRoute>
            } />

            {/* Public card routes */}
            <Route path="/c/:slug" element={
              <PublicLayout minimal>
                <PublicCardPage />
              </PublicLayout>
            } />

            <Route path="/p/:slug" element={
              <PublicLayout minimal>
                <PublicCardPage />
              </PublicLayout>
            } />

            <Route path="/card/:slug" element={
              <PublicLayout minimal>
                <PublicCardPage />
              </PublicLayout>
            } />

            <Route path="/u/:slug" element={
              <PublicLayout minimal>
                <PublicCardPage />
              </PublicLayout>
            } />

            {/* Legal routes */}
            <Route path="/terms" element={
              <PublicLayout>
                <TermsPage />
              </PublicLayout>
            } />

            <Route path="/privacy" element={
              <PublicLayout>
                <PrivacyPage />
              </PublicLayout>
            } />

            <Route path="/cookies" element={
              <PublicLayout>
                <CookiesPage />
              </PublicLayout>
            } />

            {/* Catch all 404 */}
            <Route path="*" element={
              <PublicLayout>
                <NotFoundPage />
              </PublicLayout>
            } />
          </Routes>
        </Suspense>
      </div>
    </>
  );
}

export default App; 